import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';

import { useRequestData } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import { trackEvent } from 'ddc-track-event';
import Skeleton from 'wsm-skeleton';
import { setClassNames } from 'ddc-classnames-js';

import { getVehicleObj } from '../../../utilities/vehicleTracking';

// Dynamic imports for both carousel types
const LazyImageCarousel = lazy(() =>
	import(
		/* webpackChunkName: 'new-media-focused-carousel' */ 'wsm-vehicle-media-gallery'
	).then((module) => ({
		default: module.ImageCarousel
	}))
);

const LazyMediaFocusedCarousel = lazy(() =>
	import(
		/* webpackChunkName: 'new-media-focused-carousel' */ 'wsm-vehicle-media-gallery'
	).then((module) => ({
		default: module.MediaFocusedCarousel
	}))
);

const CarouselContainer = ({
	shouldFullyRenderCard,
	images = [],
	link,
	uuid,
	carouselType = 'image' // 'image' or 'mediaFocused'
}) => {
	const { widgetName, windowId } = useRequestData();

	const trackInteraction = (direction) => {
		trackEvent(widgetName, windowId, {
			element: `Photo carousel ${direction} button`,
			result: `Move to ${direction} slide`,
			uuid,
			vehicle: getVehicleObj(uuid)
		});
	};

	// Determine which carousel component to render
	const CarouselComponent =
		carouselType === 'image' ? LazyImageCarousel : LazyMediaFocusedCarousel;

	return (
		<div
			className={setClassNames([
				'vehicle-card-media-container',
				'p-3',
				// Only add single-image-media-gallery class for ImageCarousel type
				carouselType === 'image' && 'single-image-media-gallery',
				!shouldFullyRenderCard && 'm-0'
			])}
		>
			{shouldFullyRenderCard && isBrowser ? (
				<Suspense
					fallback={
						<Skeleton
							skeletonClasses="mb-4"
							aspectRatio="4by3"
							dataTestId="media-skeleton-loading"
						/>
					}
				>
					<CarouselComponent
						images={images}
						onNextClick={() => trackInteraction('next')}
						onPrevClick={() => trackInteraction('previous')}
						link={link}
						uuid={uuid}
					/>
				</Suspense>
			) : (
				<Skeleton
					skeletonClasses="mb-4"
					aspectRatio="4by3"
					dataTestId="media-skeleton"
				/>
			)}
		</div>
	);
};

CarouselContainer.propTypes = {
	shouldFullyRenderCard: PropTypes.bool,
	images: PropTypes.arrayOf(
		PropTypes.shape({
			alt: PropTypes.string,
			id: PropTypes.string,
			provider: PropTypes.string,
			title: PropTypes.string,
			uri: PropTypes.string
		})
	),
	link: PropTypes.string,
	uuid: PropTypes.string.isRequired,
	carouselType: PropTypes.oneOf(['image', 'mediaFocused'])
};

export default CarouselContainer;
